import { Injectable } from '@angular/core';
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { EMPTY, Observable, Subject, throwError } from 'rxjs';
import { Store } from '@ngrx/store';
import { catchError, tap, switchMap, filter, take, finalize } from 'rxjs/operators';
import { BaseTokenService } from '../services/base-token.service';
import { BaseAuthenticationService } from '../../modules/auth/base-auth.service';
import { BaseHttpErrorService } from '../services/base-http-error.service';
import { AuthService } from '../../../api';
import { baseAuthActions, BaseCoreState, baseSharedActions } from '../../_store';
import { HttpErrorStatus } from '../../shared/enums/http-error-status.enum';
import { BASE_BUILD_DETAILS_URL } from '../constants/base-build-details-url';
import { BASE_SKIP_ERROR_TOAST } from '../constants/base-http-context-tokens';
import { BaseToastAlertClasses } from '../../shared/enums/base-toast-alert-classes.enum';

@Injectable()
export class BaseHttpErrorsInterceptor implements HttpInterceptor {
  private AUTH_HEADER = 'Authorization';
  private isRefreshing = false;
  private refreshTokenSubject = new Subject<string>();

  constructor(
    private httpErrorService: BaseHttpErrorService,
    private store: Store<BaseCoreState>,
    private tokenService: BaseTokenService,
    private authService: BaseAuthenticationService
  ) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (this.isBuildDetailsRequest(req)) {
      return next.handle(req);
    }

    const isRefreshRequest = this.isRefreshRequest(req);
    // Callers rendering their own error UI opt out of the generic toast via HttpContext.
    const skipErrorToast = req.context.get(BASE_SKIP_ERROR_TOAST);

    return next.handle(req).pipe(
      catchError((error: HttpErrorResponse) => {
        // Handle 404s
        if (error.status === HttpErrorStatus.NotFound) {
          if (!skipErrorToast) {
            this.httpErrorService.handle(error);
          }
          return throwError(() => error);
        }

        // Handle 401 for refresh endpoint
        if (error.status === HttpErrorStatus.Unauthorized && isRefreshRequest) {
          return throwError(() => error);
        }

        // Handle 401 for regular API calls (refresh logic)
        if (error.status === HttpErrorStatus.Unauthorized && !isRefreshRequest) {
          return this.handle401Error(req, next, error);
        }

        // Handle network issues and non-refresh errors
        if ((error.status === 0 || !isRefreshRequest) && !skipErrorToast) {
          this.httpErrorService.handle(error);
        }

        return throwError(() => error);
      })
    );
  }

  private handle401Error(
    req: HttpRequest<any>,
    next: HttpHandler,
    originalError: HttpErrorResponse
  ): Observable<HttpEvent<any>> {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject = new Subject<string>();

      const refreshToken = this.tokenService.getRefreshToken();

      if (this.isLogoutRequest(req) || !refreshToken) {
        this.isRefreshing = false;
        this.store.dispatch(
          baseSharedActions.addSystemAlert({
            payload: {
              class: BaseToastAlertClasses.error,
              body: originalError.error?.message || 'Unauthorized',
            },
          })
        );
        this.store.dispatch(baseAuthActions.logout());
        return throwError(() => originalError);
      }

      return this.authService.refresh(refreshToken).pipe(
        tap((response) => {
          this.tokenService.setTokens(response);
          this.refreshTokenSubject.next(response.token);
          this.store.dispatch(baseAuthActions.successRefreshUser());
        }),
        switchMap((response) => {
          this.isRefreshing = false;
          return next.handle(
            req.clone({
              headers: req.headers.set(this.AUTH_HEADER, `Bearer ${response.token}`),
            })
          );
        }),
        catchError((err) => {
          this.refreshTokenSubject.error(err);
          this.store.dispatch(baseAuthActions.logout());
          return EMPTY;
        }),
        finalize(() => {
          this.isRefreshing = false;
          this.refreshTokenSubject.complete();
          this.refreshTokenSubject = new Subject<string>();
        })
      );
    } else {
      return this.refreshTokenSubject.pipe(
        filter((token) => !!token),
        take(1),
        switchMap((token) =>
          next.handle(
            req.clone({
              headers: req.headers.set('Authorization', `Bearer ${token}`),
            })
          )
        ),
        catchError(() => {
          this.store.dispatch(baseAuthActions.logout());
          return EMPTY;
        })
      );
    }
  }

  private isRefreshRequest(req: HttpRequest<any>): boolean {
    return req.url.includes(AuthService.PostAppApiAuthAuthTokenrefreshingPath);
  }

  private isLogoutRequest(req: HttpRequest<any>): boolean {
    return req.url.includes(AuthService.PostAppApiAuthAuthLogoutPath);
  }

  private isBuildDetailsRequest(req: HttpRequest<any>): boolean {
    return req.url.includes(BASE_BUILD_DETAILS_URL);
  }
}
